import React, { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Search, PenSquare, Pin, Users } from 'lucide-react';
import Avatar from '../Avatar';
import SpotifyIcon from '../spotify/SpotifyIcon';
import { useSpotify } from '../../contexts/SpotifyContext';
import { parseApiDate } from '../../utils/time';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'groups', label: 'Groups' },
  { id: 'requested', label: 'Requested' },
];

function isRequestItem(item) {
  return item?.kind === 'pending_request' || item?.kind === 'group_invite';
}

function activityTime(c) {
  const raw = c.last_message?.sent_at || c.updated_at || c.created_at || 0;
  const parsed = parseApiDate(raw);
  return parsed ? parsed.getTime() : new Date(raw).getTime() || 0;
}

const ConversationList = ({
  items,
  conversations = [],
  activeId,
  onSelect,
  onNewChat,
  search = '',
  onSearchChange,
  loading = false,
}) => {
  const [localSearch, setLocalSearch] = useState(search);
  const [filter, setFilter] = useState('all');
  const { peerActivity } = useSpotify();

  const source = Array.isArray(items) ? items : conversations;

  const counts = useMemo(() => {
    let unread = 0;
    let groups = 0;
    let requested = 0;
    for (const c of source) {
      if (isRequestItem(c)) {
        requested += 1;
        continue;
      }
      if ((c.unread_count || 0) > 0) unread += 1;
      if (c.type === 'group') groups += 1;
    }
    return { unread, groups, requested };
  }, [source]);

  const filtered = useMemo(() => {
    const q = (onSearchChange ? search : localSearch).trim().toLowerCase();
    let list = source.filter((c) => {
      if (filter === 'all') return !isRequestItem(c);
      if (filter === 'unread') return !isRequestItem(c) && (c.unread_count || 0) > 0;
      if (filter === 'groups') return !isRequestItem(c) && c.type === 'group';
      if (filter === 'requested') return isRequestItem(c);
      return true;
    });

    if (q) {
      list = list.filter((c) => {
        const name =
          c.name ||
          c.other_user?.full_name ||
          c.other_user?.username ||
          c.group_name ||
          '';
        const preview = c.last_message?.text || c.preview || '';
        return name.toLowerCase().includes(q) || preview.toLowerCase().includes(q);
      });
    }

    return [...list].sort((a, b) => {
      if (filter !== 'requested') {
        const aPin = a.is_pinned ? 0 : 1;
        const bPin = b.is_pinned ? 0 : 1;
        if (aPin !== bPin) return aPin - bPin;
      }
      return activityTime(b) - activityTime(a);
    });
  }, [source, filter, search, localSearch, onSearchChange]);

  const handleSearch = (value) => {
    setLocalSearch(value);
    onSearchChange?.(value);
  };

  const emptyCopy =
    filter === 'requested'
      ? 'No chat requests'
      : filter === 'unread'
        ? 'No unread chats'
        : filter === 'groups'
          ? 'No groups yet'
          : 'No conversations yet';

  return (
    <div className="flex flex-col h-full" data-testid="conversation-list">
      <div className="p-3 border-b border-secondary-200 dark:border-secondary-700 space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="search"
              value={onSearchChange ? search : localSearch}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search conversations…"
              className="w-full pl-9 pr-3 py-2.5 min-h-[44px] rounded-lg border border-secondary-200 bg-secondary-50 text-sm text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
            />
          </div>
          {onNewChat && (
            <button
              type="button"
              onClick={onNewChat}
              className="shrink-0 p-2.5 min-h-[44px] min-w-[44px] rounded-full text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800 transition-colors"
              aria-label="New chat"
              data-testid="compose-chat-button"
              title="New chat"
            >
              <PenSquare className="h-5 w-5" />
            </button>
          )}
        </div>

        <div
          className="flex items-center gap-1.5 overflow-x-auto pb-0.5 -mx-0.5 px-0.5 scrollbar-thin"
          data-testid="conversation-filters"
          role="tablist"
          aria-label="Conversation filters"
        >
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count =
              f.id === 'unread'
                ? counts.unread
                : f.id === 'groups'
                  ? counts.groups
                  : f.id === 'requested'
                    ? counts.requested
                    : 0;
            const showCount = f.id !== 'all' && count > 0;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-transparent text-secondary-600 border-secondary-200 hover:bg-secondary-50 dark:text-secondary-300 dark:border-secondary-600 dark:hover:bg-secondary-800'
                }`}
              >
                {f.label}
                {showCount ? (
                  <span
                    className={
                      active
                        ? 'opacity-90'
                        : f.id === 'requested'
                          ? 'text-amber-600 dark:text-amber-400'
                          : ''
                    }
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="p-4 text-sm text-secondary-500 text-center">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-secondary-500 dark:text-secondary-400">
            {emptyCopy}
          </div>
        )}
        {filtered.map((item) => {
          const isGroup = item.type === 'group' || item.kind === 'group_invite';
          const name =
            item.name ||
            item.group_name ||
            item.other_user?.full_name ||
            item.other_user?.username ||
            'Unknown';
          const isPending = isRequestItem(item);
          const preview =
            (item.pending_invites?.length ? item.preview : null) ||
            item.last_message?.text ||
            item.preview ||
            (isPending ? 'Chat request pending' : 'No messages yet');
          const unread = item.unread_count || 0;
          const active = item.id === activeId;
          const timeSource =
            item.last_message?.sent_at || item.updated_at || item.created_at;
          const otherUserId = item.other_user?.id;
          const listening =
            !isGroup &&
            !isPending &&
            otherUserId &&
            peerActivity?.[otherUserId]?.is_playing &&
            peerActivity?.[otherUserId]?.track_name;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect?.(item)}
              className={`w-full flex items-center gap-3 px-3 py-3 min-h-[56px] text-left transition-colors border-b border-secondary-100 dark:border-secondary-700/60 ${
                active
                  ? 'bg-primary-50 dark:bg-primary-900/20'
                  : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/80'
              }`}
              data-testid={
                isPending
                  ? item.kind === 'group_invite'
                    ? 'group-invite-row'
                    : 'pending-request-row'
                  : 'conversation-row'
              }
            >
              {isGroup ? (
                <div className="h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4" />
                </div>
              ) : (
                <Avatar
                  blobName={item.other_user?.avatar}
                  alt={name}
                  size="sm"
                  className="shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`text-sm truncate inline-flex items-center gap-1 ${
                      unread || isPending
                        ? 'font-semibold text-secondary-900 dark:text-secondary-50'
                        : 'font-medium text-secondary-800 dark:text-secondary-100'
                    }`}
                  >
                    {item.is_pinned && (
                      <Pin
                        className="h-3 w-3 text-primary-600 dark:text-primary-400 shrink-0"
                        data-testid="pinned-chat-icon"
                      />
                    )}
                    {name}
                    {listening ? (
                      <span
                        className="inline-flex shrink-0"
                        data-testid="conversation-listening-icon"
                      >
                        <SpotifyIcon
                          className="h-3 w-3 text-[#1DB954]"
                          title={`Listening to ${peerActivity[otherUserId].track_name}`}
                        />
                      </span>
                    ) : null}
                  </span>
                  {timeSource && (
                    <span
                      className={`text-[10px] shrink-0 ${
                        unread > 0
                          ? 'text-primary-600 dark:text-primary-400 font-medium'
                          : 'text-secondary-400'
                      }`}
                    >
                      {formatDistanceToNow(parseApiDate(timeSource) || new Date(), {
                        addSuffix: false,
                      })}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p
                    className={`text-xs truncate ${
                      unread > 0
                        ? 'text-secondary-800 dark:text-secondary-200 font-medium'
                        : 'text-secondary-500 dark:text-secondary-400'
                    }`}
                  >
                    {isPending && (
                      <span className="text-amber-600 dark:text-amber-400 font-medium mr-1">
                        {item.kind === 'group_invite'
                          ? 'Group invite ·'
                          : item.requestDirection === 'outgoing'
                            ? 'Sent ·'
                            : 'Pending ·'}
                      </span>
                    )}
                    {preview}
                  </p>
                  {isPending ? (
                    <span className="shrink-0 inline-flex items-center justify-center px-1.5 h-[18px] rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] font-semibold">
                      {item.kind === 'group_invite' ? 'Invite' : 'Request'}
                    </span>
                  ) : item.pending_invites?.length > 0 ? (
                    <span
                      className="shrink-0 inline-flex items-center justify-center px-1.5 h-[18px] rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] font-semibold"
                      data-testid="pending-invites-badge"
                    >
                      {item.pending_invites.length} pending
                    </span>
                  ) : unread > 0 ? (
                    <span
                      className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-primary-600 text-white text-[10px] font-semibold"
                      data-testid="unread-badge"
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  ) : null}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ConversationList;
